/**
 * HipaaSourceVerificationsSection
 *
 * Shows every knowledge-source that was automatically HIPAA-verified, with
 * per-source overall status and the full list of control results (pass/fail
 * + failure reason for each check).
 *
 * Manual controls additionally show:
 *   - The required evidence description
 *   - An inline evidence-reference input + "Save Evidence" action
 *   - A deep-link badge to the corresponding HIPAA Compliance Center section
 *
 * Compliance Decision Workflow:
 *   Blocked sources show a structured panel:
 *     HIPAA Status → Reason → Failed Controls → Current Decision → Available Actions
 *   Actions (compliance.manage only):
 *     - Fix Required   → source stays blocked; reason required
 *     - Accept Risk    → justification + business reason + optional evidence reference
 *     - Approve Exception → reason + expiry (required) + auto-named approver
 *     - Keep Blocked   → reason required
 *   After decision: prominent badge with key details.
 *   Decision history is collapsible below the control list.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, XCircle, Clock, RefreshCcw, ChevronDown, ChevronUp, ChevronRight,
  Link2, ExternalLink, ShieldCheck, ShieldAlert, ShieldOff, AlertTriangle,
  History, Wrench, Info, FileText, Upload, Paperclip, Download,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Auth helper ───────────────────────────────────────────────────────────────
function useHipaaFetch() {
  return (url: string, opts: RequestInit = {}) => {
    const token =
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      "";
    return fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers as Record<string, string> || {}),
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  };
}

// Decode the JWT to pre-fill reviewer name / email
function useCurrentUserInfo(): { email: string; displayName: string } {
  try {
    const token =
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      "";
    if (!token) return { email: "", displayName: "" };
    const payload = JSON.parse(atob(token.split(".")[1]));
    const email =
      payload.email ||
      payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ||
      "";
    const displayName = payload.username || payload.name || email.split("@")[0] || "";
    return { email, displayName };
  } catch {
    return { email: "", displayName: "" };
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface ControlResult {
  id: string;
  controlKey: string;
  controlName: string;
  verificationMethod: "automatic" | "manual_required" | string;
  complianceStatus: "passed" | "failed" | "manual_required" | "not_applicable" | string;
  isRequired: boolean;
  findings: string | null;
  evidenceRequired: string | null;
  evidenceReference: string | null;
  verifiedAt: string | null;
  linkedEntityId: string | null;
  linkedEntityType: string | null;
}

export interface SourceVerification {
  id: string;
  knowledgeSourceId: string;
  organizationId: string;
  chatbotId: string;
  sourceName: string;
  sourceType: string;
  overallStatus: "pending_verification" | "active" | "blocked" | "pending_review" | "allowed" | string;
  verificationStartedAt: string | null;
  verificationCompletedAt: string | null;
  verificationSummary: string | null;
  passedCount: number;
  failedCount: number;
  manualRequiredCount: number;
  createdAt: string;
  updatedAt: string;
  controlResults: ControlResult[];
}

interface ComplianceDecision {
  id: string;
  decisionType: "remediation_required" | "accept_risk" | "exception_approved" | "keep_blocked" | string;
  justification: string | null;
  approverName: string | null;
  approverTitle: string | null;
  approvalDate: string | null;
  expirationDate: string | null;
  previousStatus: string;
  newStatus: string;
  createdByEmail: string;
  createdAt: string;
  isExpired?: boolean;
  expiredAt?: string | null;
}

type DecisionModal = "fix_required" | "accept_risk" | "exception_approved" | "keep_blocked" | null;

// Maps EntityType → human label (no internal IDs shown to users)
const ENTITY_TYPE_LABELS: Record<string, string> = {
  HipaaDeviceMediaControl:             "Device & Media Controls",
  HipaaAccessControlPolicy:            "Access Control Policies",
  HipaaPhiAuditControl:                "PHI Audit Logging",
  HipaaMinNecessaryAccessPolicy:       "Minimum Necessary Access",
  HipaaBusinessAssociate:              "Business Associates (BAA)",
  HipaaPhiEncryptionAtRest:            "Encryption at Rest",
  HipaaRiskAssessment:                 "Risk Assessment",
  HipaaBreachNotification:             "Breach Notification",
  HipaaWorkforceAccess:                "Workforce HIPAA Training",
  HipaaTransmissionSecurityControl:    "Transmission Security",
  HipaaCspSecurityControl:             "Content-Security-Policy",
  HipaaXssSecurityControl:             "XSS Prevention",
  HipaaBrowserDataProtectionControl:   "Browser Data Protection",
};

// ── Status helpers ────────────────────────────────────────────────────────────
function statusBadge(status: string) {
  if (status === "active")               return <Badge variant="default"    className="text-xs">✅ Verified</Badge>;
  if (status === "blocked")              return <Badge variant="destructive" className="text-xs">🔴 Blocked</Badge>;
  if (status === "allowed")              return <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Risk Accepted</Badge>;
  if (status === "pending_review")       return <Badge variant="secondary"  className="text-xs">⏳ Pending Review</Badge>;
  if (status === "pending_verification") return <Badge variant="secondary"  className="text-xs">⏳ Verifying…</Badge>;
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

function decisionTypeLabel(type: string) {
  if (type === "accept_risk")          return "Risk Accepted";
  if (type === "exception_approved")   return "Exception Approved";
  if (type === "remediation_required") return "Fix Required";
  if (type === "keep_blocked")         return "Kept Blocked";
  return type;
}

function decisionTypeBadge(type: string, expired?: boolean) {
  if (type === "accept_risk")
    return <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Risk Accepted</Badge>;
  if (type === "exception_approved")
    return expired
      ? <Badge variant="destructive" className="text-xs">Exception Expired</Badge>
      : <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600">🟢 Exception Approved</Badge>;
  if (type === "remediation_required")
    return <Badge variant="destructive" className="text-xs">Fix Required</Badge>;
  if (type === "keep_blocked")
    return <Badge variant="outline" className="text-xs">🔴 Kept Blocked</Badge>;
  return <Badge variant="outline" className="text-xs">{type}</Badge>;
}

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

// ── Structured evidence types ─────────────────────────────────────────────────
const EVIDENCE_TYPES = [
  { value: "policy_document",       label: "Policy Document" },
  { value: "procedure",             label: "Procedure" },
  { value: "screenshot",            label: "Screenshot" },
  { value: "configuration_record",  label: "Configuration Record" },
  { value: "audit_report",          label: "Audit Report" },
  { value: "external_reference",    label: "External Reference" },
  { value: "other",                 label: "Other" },
] as const;

// ── Dynamic description placeholders per HIPAA control key ────────────────────
const CONTROL_PLACEHOLDERS: Record<string, { placeholder: string; hint: string }> = {
  access_control: {
    placeholder: "Describe how access permissions are managed, reviewed, and approved for users accessing this source.",
    hint: "e.g. Role-based access control policy defines user permissions, approval workflow, and periodic access reviews.",
  },
  audit_logging: {
    placeholder: "Describe how PHI access events are logged, monitored, and reviewed.",
    hint: "e.g. Audit logs capture PHI access attempts with timestamps, user identity, and review process.",
  },
  breach_notification: {
    placeholder: "Describe the documented breach response and notification process.",
    hint: "e.g. Incident response procedure defines breach assessment, reporting timeline, and notification workflow.",
  },
  business_associate: {
    placeholder: "Describe the agreement, vendor responsibilities, and compliance obligations.",
    hint: "e.g. BAA document defines permitted PHI usage, security responsibilities, and compliance requirements.",
  },
  encryption_at_rest: {
    placeholder: "Describe encryption mechanisms protecting stored PHI data.",
    hint: "e.g. AES-256 encryption is applied to stored PHI with managed encryption keys.",
  },
  transmission_security: {
    placeholder: "Describe how PHI transmission is secured during data transfer.",
    hint: "e.g. TLS encryption protects PHI during transmission between systems.",
  },
  csp_headers: {
    placeholder: "Describe security headers or policies configured to protect this source.",
    hint: "e.g. Content-Security-Policy headers are configured to prevent unauthorized script execution.",
  },
  xss_prevention: {
    placeholder: "Describe X-Content-Type-Options and XSS prevention controls in place.",
    hint: "e.g. nosniff header prevents MIME-type sniffing; input sanitization prevents script injection.",
  },
  browser_data_protection: {
    placeholder: "Describe cache control settings that prevent PHI from being stored in browser caches.",
    hint: "e.g. Cache-Control: no-store header ensures PHI responses are never cached locally.",
  },
  phi_classification: {
    placeholder: "Describe how PHI is identified, classified, and labelled in this knowledge source.",
    hint: "e.g. Data classification policy identifies all PHI fields and applies sensitivity labels.",
  },
  minimum_necessary: {
    placeholder: "Describe how access to PHI is limited to only what is required for the intended purpose.",
    hint: "e.g. Role-based restrictions limit data exposure to only fields necessary for each function.",
  },
  workforce_training: {
    placeholder: "Describe HIPAA training records and completion evidence for staff with PHI access.",
    hint: "e.g. Annual HIPAA security training completed by all staff; certificates attached.",
  },
  risk_assessment: {
    placeholder: "Describe the risk assessment scope, methodology, findings, and remediation plan.",
    hint: "e.g. Annual HIPAA risk analysis identifies threats to PHI confidentiality, integrity, and availability.",
  },
};

const DEFAULT_PLACEHOLDER = {
  placeholder: "Describe the evidence, policy, or control measure supporting HIPAA compliance for this check.",
  hint: "e.g. Policy reviewed and approved; procedure implemented and tested.",
};

// ── File attachment helpers ────────────────────────────────────────────────────
const ALLOWED_ATTACHMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
];
const ALLOWED_ATTACHMENT_EXT = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "png" || ext === "jpg" || ext === "jpeg") return "🖼";
  return "📎";
}

interface AttachmentData {
  name: string;
  size: number;
  dataUrl: string; // base64 data URL — stored inline in evidence JSON
}

interface EvidenceData {
  title: string;
  type: string;
  description: string;
  reference: string;
  attachment?: AttachmentData;
  addedBy: string;
  addedAt: string;
}

function parseEvidence(raw: string | null): EvidenceData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.title) return parsed as EvidenceData;
  } catch { /* legacy plain-text fallback handled by caller */ }
  return null;
}

function evidenceTypeLabel(type: string) {
  return EVIDENCE_TYPES.find(t => t.value === type)?.label ?? type;
}

// ── Evidence edit form (pure form — no internal open/close state) ─────────────
function EvidenceForm({
  sourceId, controlKey, currentEvidence, onSaved, onCancel,
}: {
  sourceId: string;
  controlKey: string;
  currentEvidence: string | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const authFetch = useHipaaFetch();
  const { email } = useCurrentUserInfo();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const parsed = parseEvidence(currentEvidence);

  const [title,       setTitle]       = useState(parsed?.title              ?? "");
  const [type,        setType]        = useState(parsed?.type               ?? "");
  const [description, setDescription] = useState(parsed?.description        ?? "");
  const [reference,   setReference]   = useState(parsed?.reference          ?? "");
  const [attachment,  setAttachment]  = useState<AttachmentData | null>(parsed?.attachment ?? null);

  const { placeholder, hint } = CONTROL_PLACEHOLDERS[controlKey] ?? DEFAULT_PLACEHOLDER;

  const canSave = title.trim().length > 0 && type.length > 0;

  // ── File selection ──────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      toast({ title: "Unsupported file type. Use PDF, DOC, DOCX, PNG, or JPG.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast({ title: `File too large. Maximum size is ${formatBytes(MAX_ATTACHMENT_BYTES)}.`, variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = ev => {
      setAttachment({ name: file.name, size: file.size, dataUrl: ev.target!.result as string });
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected after removal
    e.target.value = "";
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const payload: EvidenceData = {
        title: title.trim(),
        type,
        description: description.trim(),
        reference: reference.trim(),
        ...(attachment ? { attachment } : {}),
        addedBy: email || "—",
        addedAt: new Date().toISOString(),
      };
      const r = await authFetch(
        `/api/organization/compliance/hipaa/source-verifications/${sourceId}/controls/${controlKey}`,
        { method: "PATCH", body: JSON.stringify({ evidenceReference: JSON.stringify(payload) }) },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast({ title: (err as any).message ?? "Failed to save evidence.", variant: "destructive" });
      } else {
        toast({ title: "Evidence saved — control marked PASSED." });
        onSaved();
      }
    } catch {
      toast({ title: "Network error saving evidence.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded border border-dashed border-blue-300 bg-blue-50/50 p-3 space-y-3"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5" aria-hidden="true" />
        {currentEvidence ? "Edit Evidence" : "Add Evidence"}
      </p>

      {/* Title */}
      <div>
        <Label className="text-xs font-medium">Evidence Title <span className="text-red-500">*</span></Label>
        <Input
          className="mt-0.5 h-7 text-xs"
          placeholder="e.g. Access Control Policy v2"
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={200}
          onClick={e => e.stopPropagation()}
        />
      </div>

      {/* Type */}
      <div>
        <Label className="text-xs font-medium">Evidence Type <span className="text-red-500">*</span></Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="mt-0.5 h-7 text-xs" onClick={e => e.stopPropagation()}>
            <SelectValue placeholder="Select type…" />
          </SelectTrigger>
          <SelectContent>
            {EVIDENCE_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Evidence Reference: URL + optional file upload */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Evidence Reference</Label>

        {/* URL field */}
        <div>
          <p className="text-[10px] text-muted-foreground mb-0.5">URL / Document Reference</p>
          <Input
            className="h-7 text-xs font-mono"
            placeholder="Enter URL or document reference"
            value={reference}
            onChange={e => setReference(e.target.value)}
            maxLength={1000}
            onClick={e => e.stopPropagation()}
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-muted" />
          <span className="text-[10px] text-muted-foreground font-medium">OR</span>
          <div className="flex-1 border-t border-muted" />
        </div>

        {/* File upload */}
        <div>
          <p className="text-[10px] text-muted-foreground mb-1">Attachment</p>
          {attachment ? (
            /* Attached file preview */
            <div className="flex items-center gap-2 rounded border border-blue-200 bg-white px-2.5 py-1.5 text-xs">
              <span className="text-base leading-none" aria-hidden="true">{fileIcon(attachment.name)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{attachment.name}</p>
                <p className="text-muted-foreground text-[10px]">{formatBytes(attachment.size)}</p>
              </div>
              <Button
                size="sm" variant="ghost"
                className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-1.5 flex-shrink-0"
                onClick={e => { e.stopPropagation(); setAttachment(null); }}
              >
                Remove
              </Button>
            </div>
          ) : (
            /* Upload trigger */
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 rounded border border-dashed border-muted-foreground/40 bg-white hover:border-blue-400 hover:bg-blue-50/30 transition-colors px-3 py-2 text-xs text-muted-foreground hover:text-blue-700"
              onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
            >
              <Upload className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Upload File</span>
            </button>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_ATTACHMENT_EXT}
            className="hidden"
            onChange={handleFileChange}
            onClick={e => e.stopPropagation()}
          />

          {/* Accepted formats note */}
          {!attachment && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Supported: PDF, DOC, DOCX, PNG, JPG · Max {formatBytes(MAX_ATTACHMENT_BYTES)}
            </p>
          )}
        </div>
      </div>

      {/* Description — dynamic placeholder per control */}
      <div>
        <Label className="text-xs font-medium">Description / Notes</Label>
        <Textarea
          className="mt-0.5 text-xs"
          rows={3}
          placeholder={placeholder}
          value={description}
          onChange={e => setDescription(e.target.value)}
          maxLength={2000}
          onClick={e => e.stopPropagation()}
        />
        {!description && hint && (
          <p className="text-[10px] text-muted-foreground/70 mt-0.5 italic">{hint}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          size="sm" className="h-7 text-xs px-3"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving…" : "Save Evidence"}
        </Button>
        <Button
          size="sm" variant="outline" className="h-7 text-xs"
          onClick={e => { e.stopPropagation(); onCancel(); }}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Saved evidence read-only card ─────────────────────────────────────────────
function EvidenceCard({
  currentEvidence, onEdit, canManage,
}: {
  currentEvidence: string; onEdit: () => void; canManage: boolean;
}) {
  const parsed = parseEvidence(currentEvidence);

  if (!parsed) {
    // Legacy plain-text fallback
    return (
      <div className="rounded border bg-muted/30 px-2.5 py-2 text-xs" onClick={e => e.stopPropagation()}>
        <p className="font-medium mb-0.5">Evidence:</p>
        <p className="text-muted-foreground break-words">{currentEvidence}</p>
        {canManage && (
          <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs px-1.5"
            onClick={e => { e.stopPropagation(); onEdit(); }}>
            Edit
          </Button>
        )}
      </div>
    );
  }

  const isUrl = (s: string) => /^https?:\/\//.test(s);

  return (
    <div
      className="rounded border border-blue-200 bg-blue-50/40 px-2.5 py-2.5 text-xs space-y-2"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-blue-900 flex items-center gap-1.5">
          <FileText className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
          Evidence
        </p>
        {canManage && (
          <Button
            size="sm" variant="ghost"
            className="h-6 text-xs px-1.5 flex-shrink-0 text-blue-700 hover:text-blue-900"
            onClick={e => { e.stopPropagation(); onEdit(); }}
          >
            Edit Evidence
          </Button>
        )}
      </div>

      {/* Label / value grid */}
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
        {parsed.title && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Title:</span>
            <span className="text-foreground break-words">{parsed.title}</span>
          </>
        )}
        {parsed.type && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Type:</span>
            <span className="text-foreground">{evidenceTypeLabel(parsed.type)}</span>
          </>
        )}
        {parsed.description && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Description:</span>
            <span className="text-foreground break-words">{parsed.description}</span>
          </>
        )}
        {parsed.reference && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Reference:</span>
            {isUrl(parsed.reference) ? (
              <a
                href={parsed.reference}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline break-all flex items-center gap-1"
              >
                {parsed.reference}
                <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" aria-hidden="true" />
              </a>
            ) : (
              <span className="text-foreground break-words">{parsed.reference}</span>
            )}
          </>
        )}
        {parsed.attachment && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap col-span-2 mt-1 border-t border-blue-100 pt-2">
              Attachment
            </span>
            <div className="col-span-2 rounded border border-blue-100 bg-white/60 px-2.5 py-2 flex flex-col gap-1.5"
              onClick={e => e.stopPropagation()}>
              {/* Filename row */}
              <div className="flex items-center gap-1.5">
                <span className="text-base leading-none flex-shrink-0" aria-hidden="true">
                  {fileIcon(parsed.attachment.name)}
                </span>
                <span className="font-medium text-foreground truncate">{parsed.attachment.name}</span>
              </div>
              {/* Size row */}
              <div className="flex items-center gap-1 text-muted-foreground">
                <span className="font-bold text-foreground">Size:</span>
                <span>{formatBytes(parsed.attachment.size)}</span>
              </div>
              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-0.5">
                <a
                  href={parsed.attachment.dataUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-blue-700 hover:bg-blue-100 transition-colors"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  View
                </a>
                <a
                  href={parsed.attachment.dataUrl}
                  download={parsed.attachment.name}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-50 px-2 py-0.5 text-slate-700 hover:bg-slate-100 transition-colors"
                  onClick={e => e.stopPropagation()}
                >
                  <Download className="w-3 h-3" aria-hidden="true" />
                  Download
                </a>
              </div>
            </div>
          </>
        )}
        {parsed.addedBy && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Added By:</span>
            <span className="text-foreground break-words">{parsed.addedBy}</span>
          </>
        )}
        {parsed.addedAt && (
          <>
            <span className="font-bold text-foreground whitespace-nowrap">Added Date:</span>
            <span className="text-foreground">{fmt(parsed.addedAt)}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Evidence section — collapsible, with independent edit state ───────────────
function EvidenceSection({
  sourceId, controlKey, currentEvidence, canManage, onSaved,
}: {
  sourceId: string;
  controlKey: string;
  currentEvidence: string | null;
  canManage: boolean;
  onSaved: () => void;
}) {
  // Collapsed by default; each instance is independent
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(false);

  // When evidence is saved or cleared, close edit mode and stay expanded
  const handleSaved = () => {
    setEditing(false);
    onSaved();
  };

  // When user cancels editing, return to read-only evidence view
  const handleCancel = () => {
    setEditing(false);
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      {/* Collapsible toggle row */}
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1 w-full text-left"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); if (open) setEditing(false); }}
      >
        {open
          ? <ChevronDown  className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />}
        Evidence
        {currentEvidence && !open && (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
            Saved
          </span>
        )}
        {!currentEvidence && canManage && !open && (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
            Required
          </span>
        )}
      </button>

      {/* Expanded content */}
      {open && (
        <div className="mt-1 space-y-2" onClick={e => e.stopPropagation()}>
          {/* Read-only saved card (when not editing) */}
          {currentEvidence && !editing && (
            <EvidenceCard
              currentEvidence={currentEvidence}
              onEdit={() => setEditing(true)}
              canManage={canManage}
            />
          )}

          {/* Edit form — opened by Edit Evidence button OR shown directly when no evidence yet */}
          {canManage && (editing || !currentEvidence) && (
            <EvidenceForm
              sourceId={sourceId}
              controlKey={controlKey}
              currentEvidence={currentEvidence}
              onSaved={handleSaved}
              onCancel={editing ? handleCancel : () => setOpen(false)}
            />
          )}

          {/* Read-only viewer with no evidence, non-manager */}
          {!canManage && !currentEvidence && (
            <p className="text-xs text-muted-foreground italic py-1">No evidence submitted.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Compliance control card (replaces flat row) ───────────────────────────────
function ControlComplianceCard({
  r, sourceId, canManage, onEvidenceSaved,
}: {
  r: ControlResult; sourceId: string; canManage: boolean; onEvidenceSaved: () => void;
}) {
  const passed        = r.complianceStatus === "passed";
  const failed        = r.complianceStatus === "failed";
  const notApplicable = r.complianceStatus === "not_applicable";
  const isManual      = r.verificationMethod === "manual_required";
  const needsEvidence = r.complianceStatus === "manual_required";
  const linkedLabel   = r.linkedEntityType ? ENTITY_TYPE_LABELS[r.linkedEntityType] ?? r.linkedEntityType : null;

  const cardBorder =
    failed        ? "border-red-200 bg-red-50/40" :
    needsEvidence ? "border-amber-200 bg-amber-50/40" :
    passed        ? "border-green-100 bg-green-50/20" :
                    "border-muted bg-muted/10";

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${cardBorder}`}>
      {/* Header row: name + badges */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {passed        && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0"        aria-hidden="true" />}
          {failed        && <XCircle      className="w-4 h-4 text-red-600 flex-shrink-0"          aria-hidden="true" />}
          {needsEvidence && <Clock        className="w-4 h-4 text-amber-500 flex-shrink-0"        aria-hidden="true" />}
          {notApplicable && <CheckCircle2 className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />}
          <span className={`font-semibold text-xs ${failed ? "text-red-800" : needsEvidence ? "text-amber-800" : ""}`}>
            {r.controlName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {passed        && <Badge className="text-[10px] h-5 px-1.5 bg-green-600 hover:bg-green-600">PASSED</Badge>}
          {failed        && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">FAILED</Badge>}
          {needsEvidence && <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">PENDING</Badge>}
          {notApplicable && <Badge variant="outline" className="text-[10px] h-5 px-1.5">N/A</Badge>}
          <Badge variant="outline" className={`text-[10px] h-5 px-1.5 ${isManual ? "border-blue-300 text-blue-700" : "border-muted text-muted-foreground"}`}>
            {isManual ? "Manual" : "Automatic"}
          </Badge>
        </div>
      </div>

      {/* Finding details */}
      {failed && r.findings && (
        <div className="rounded bg-red-50 border border-red-200 px-2.5 py-1.5 text-xs">
          <p className="font-medium text-red-800 mb-0.5">Finding:</p>
          <p className="text-red-700">{r.findings}</p>
        </div>
      )}

      {/* Evidence required description */}
      {needsEvidence && r.evidenceRequired && (
        <div className="rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
          <span className="font-medium">Required: </span>{r.evidenceRequired}
        </div>
      )}

      {/* Control reference link */}
      {linkedLabel && r.linkedEntityId && (
        <p className="flex items-center gap-1 text-xs text-blue-700">
          <ExternalLink className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
          Control Reference: <span className="font-medium">{linkedLabel}</span>
        </p>
      )}

      {/* Evidence section (manual controls) */}
      {isManual && (
        <EvidenceSection
          sourceId={sourceId}
          controlKey={r.controlKey}
          currentEvidence={r.evidenceReference}
          canManage={canManage}
          onSaved={onEvidenceSaved}
        />
      )}
    </div>
  );
}

// Keep ControlResultRow as an alias for backward compat (used in DecisionPanel)
const ControlResultRow = ControlComplianceCard;

// ── Collapsible Decision History ──────────────────────────────────────────────
function DecisionHistoryPanel({ verificationId }: { verificationId: string }) {
  const authFetch = useHipaaFetch();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery<ComplianceDecision[]>({
    queryKey: ["hipaa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/hipaa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000,
    enabled: open,
  });

  if (isLoading && open) return <Skeleton className="h-16 w-full" />;
  const count = data?.length ?? 0;

  return (
    <div className="mt-2">
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <History className="w-3.5 h-3.5" aria-hidden="true" />
        Decision History{count > 0 ? ` (${count})` : ""}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && data && data.length === 0 && (
        <p className="text-xs text-muted-foreground mt-1 ml-5">No decisions recorded yet.</p>
      )}

      {open && data && data.length > 0 && (
        <div className="mt-2 rounded border bg-background divide-y">
          {data.map(d => (
            <div key={d.id} className="px-3 py-2 text-xs space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                {decisionTypeBadge(d.decisionType, d.isExpired)}
                <span className="text-muted-foreground text-[10px]">
                  {new Date(d.createdAt).toLocaleDateString(undefined, {
                    year: "numeric", month: "short", day: "numeric",
                  })}
                </span>
                {d.isExpired && d.expiredAt && (
                  <span className="text-red-600 text-[10px]">
                    Expired {new Date(d.expiredAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              {d.approverName && (
                <p className="text-muted-foreground">
                  Approved by <span className="font-medium">{d.approverName}</span>
                  {d.approverTitle ? ` (${d.approverTitle})` : ""}
                  {d.approvalDate ? ` on ${fmt(d.approvalDate)}` : ""}
                  {d.expirationDate ? ` · expires ${fmt(d.expirationDate)}` : ""}
                </p>
              )}
              {d.justification && (
                <p className="text-muted-foreground italic">"{d.justification}"</p>
              )}
              <p className="text-muted-foreground text-[10px]">By: {d.createdByEmail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Justification parser (structured storage from AcceptRiskDialog) ────────────
interface ParsedJustification {
  justification: string;
  businessReason: string;
  evidenceRef: string;
  reviewDate: string;   // Risk Review Date (Next Review Date)
  validUntil: string;   // Risk Acceptance Valid Until
}

function parseJustification(raw: string | null): ParsedJustification {
  if (!raw) return { justification: "", businessReason: "", evidenceRef: "", reviewDate: "", validUntil: "" };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && ("justification" in parsed || "businessReason" in parsed)) {
      return {
        justification:  parsed.justification  ?? "",
        businessReason: parsed.businessReason ?? "",
        evidenceRef:    parsed.evidenceRef    ?? "",
        reviewDate:     parsed.reviewDate     ?? "",
        validUntil:     parsed.validUntil     ?? "",
      };
    }
  } catch { /* legacy string */ }
  // Legacy: parse concatenated format "Justification\n\nBusiness reason: …\n\nEvidence: …"
  const brMatch = raw.match(/\n\nBusiness reason:\s*([\s\S]*?)(?:\n\nEvidence:|$)/);
  const evMatch = raw.match(/\n\nEvidence:\s*([\s\S]*)$/);
  const justPart = raw.split("\n\nBusiness reason:")[0];
  return {
    justification:  justPart?.trim()         ?? raw,
    businessReason: brMatch?.[1]?.trim()     ?? "",
    evidenceRef:    evMatch?.[1]?.trim()     ?? "",
    reviewDate:     "",
    validUntil:     "",
  };
}

// ── After-decision status display ─────────────────────────────────────────────
function CurrentDecisionPanel({ verificationId }: { verificationId: string }) {
  const authFetch = useHipaaFetch();
  const { data } = useQuery<ComplianceDecision[]>({
    queryKey: ["hipaa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/hipaa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000,
  });

  const latest = data?.[0];
  if (!latest) return null;

  const dt = latest.decisionType;
  const expired = latest.isExpired;

  if (dt === "accept_risk") {
    const { justification, businessReason, reviewDate, validUntil } = parseJustification(latest.justification);
    // reviewDate stored in JSON; validUntil stored in JSON AND as expirationDate in DB
    const displayReviewDate  = reviewDate  || (latest.expirationDate ? "" : "");
    const displayValidUntil  = validUntil  || latest.expirationDate || "";
    return (
      <div className="rounded border border-amber-300 bg-amber-50 px-3 py-3 text-xs space-y-2">
        {/* Header */}
        <p className="font-semibold text-amber-800 flex items-center gap-1.5 text-sm">
          <ShieldAlert className="w-4 h-4" aria-hidden="true" />
          Compliance Decision: Risk Accepted
        </p>
        <p className="text-amber-700 text-[11px]">
          This control has an unresolved security finding. The risk has been reviewed and accepted
          by an authorized compliance user.
        </p>
        <div className="border-t border-amber-200 pt-2 space-y-1">
          <div className="flex gap-4 flex-wrap">
            <div>
              <span className="text-amber-600 font-medium">Status:</span>{" "}
              <span className="font-mono font-semibold text-red-700 uppercase">❌ FAILED</span>
            </div>
            <div>
              <span className="text-amber-600 font-medium">Decision:</span>{" "}
              <span className="font-medium text-amber-800">🟡 Risk Accepted</span>
            </div>
          </div>
          {latest.approverName && (
            <p className="text-amber-700">
              Approved By: <span className="font-medium">{latest.approverName}</span>
            </p>
          )}
          <p className="text-amber-700">
            Approval Date: <span className="font-medium">{fmt(latest.approvalDate ?? latest.createdAt)}</span>
          </p>
          <p className="text-amber-700">
            Next Review Date:{" "}
            <span className="font-medium">
              {displayReviewDate ? fmt(displayReviewDate) : "Not scheduled"}
            </span>
          </p>
          <p className="text-amber-700">
            Risk Acceptance Valid Until:{" "}
            <span className="font-medium">
              {displayValidUntil ? fmt(displayValidUntil) : "Not scheduled"}
            </span>
          </p>
          {businessReason && (
            <p className="text-amber-700">
              Business Reason: <span className="italic">{businessReason}</span>
            </p>
          )}
          {justification && (
            <p className="text-amber-700">
              Justification: <span className="italic">{justification}</span>
            </p>
          )}
        </div>
        <p className="text-amber-600 text-[10px] border-t border-amber-200 pt-1.5 font-medium">
          Status: Original security finding remains unresolved.
        </p>
      </div>
    );
  }

  if (dt === "exception_approved") {
    const color = expired ? "red" : "emerald";
    const { justification } = parseJustification(latest.justification);
    return (
      <div className={`rounded border px-3 py-3 text-xs space-y-2 ${
        expired ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"
      }`}>
        <p className={`font-semibold flex items-center gap-1.5 text-sm ${expired ? "text-red-800" : "text-emerald-800"}`}>
          <ShieldCheck className="w-4 h-4" aria-hidden="true" />
          Compliance Decision: {expired ? "Exception Expired" : "Exception Approved"}
        </p>
        <p className={`text-${color}-700`}>
          Exception Expiration Date:{" "}
          <span className="font-medium">
            {latest.expirationDate
              ? <>{fmt(latest.expirationDate)}{expired && <span className="ml-2 font-semibold text-red-700">(EXPIRED — source re-blocked)</span>}</>
              : "Not scheduled"}
          </span>
        </p>
        <p className={`text-${color}-700`}>
          Review Required Before:{" "}
          <span className="font-medium">
            {latest.expirationDate ? fmt(latest.expirationDate) : "Not scheduled"}
          </span>
        </p>
        {latest.approverName && (
          <p className={`text-${color}-700`}>
            Approved By: <span className="font-medium">{latest.approverName}</span>
            {latest.approverTitle ? ` (${latest.approverTitle})` : ""}
          </p>
        )}
        {justification && (
          <p className={`text-${color}-700`}>
            Reason: <span className="italic">{justification}</span>
          </p>
        )}
        {!expired && (
          <p className={`text-${color}-600 text-[10px] border-t border-${color}-200 pt-1.5 font-medium`}>
            This exception requires review before expiration.
          </p>
        )}
      </div>
    );
  }

  if (dt === "remediation_required") {
    return (
      <div className="rounded border border-orange-300 bg-orange-50 px-3 py-2.5 text-xs space-y-1">
        <p className="font-semibold text-orange-800 flex items-center gap-1.5">
          <Wrench className="w-3.5 h-3.5" aria-hidden="true" />
          Compliance Decision: Fix Required
        </p>
        {latest.justification && (
          <p className="text-orange-700">Reason: <span className="italic">{latest.justification}</span></p>
        )}
        <p className="text-orange-600 text-[10px]">Recorded {fmt(latest.createdAt)} by {latest.createdByEmail}</p>
      </div>
    );
  }

  if (dt === "keep_blocked") {
    return (
      <div className="rounded border border-muted bg-muted/40 px-3 py-2.5 text-xs space-y-1">
        <p className="font-semibold flex items-center gap-1.5">
          <ShieldOff className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
          Compliance Decision: Kept Blocked
        </p>
        {latest.justification && (
          <p className="text-muted-foreground">Reason: <span className="italic">{latest.justification}</span></p>
        )}
      </div>
    );
  }

  return null;
}

// ── Decision Dialogs ──────────────────────────────────────────────────────────

function FixRequiredDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (data: { justification: string }) => void;
  submitting: boolean;
}) {
  const [justification, setJustification] = useState("");
  const canSubmit = justification.trim().length >= 10;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-orange-600" aria-hidden="true" />
            Fix Required
          </DialogTitle>
          <DialogDescription>
            This will keep the source blocked from chatbot retrieval until the issues are fixed and
            the source is re-verified.
          </DialogDescription>
        </DialogHeader>
        <div className="py-1">
          <Label htmlFor="fix-reason" className="text-xs font-medium">
            Reason <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="fix-reason"
            className="mt-1 text-xs"
            rows={3}
            placeholder="Describe what needs to be fixed before this source can be used (min. 10 characters)…"
            value={justification}
            onChange={e => setJustification(e.target.value)}
            maxLength={2000}
          />
          <p className="text-xs text-muted-foreground mt-0.5">{justification.length}/2000</p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="destructive" size="sm"
            onClick={() => onSubmit({ justification })} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Mark Fix Required"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptRiskDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (data: {
    justification: string; businessReason: string;
    reviewerName: string; evidenceRef: string;
    reviewDate: string; validUntil: string;
  }) => void;
  submitting: boolean;
}) {
  const { displayName } = useCurrentUserInfo();
  const today = new Date().toISOString().slice(0, 10);

  const [justification,  setJustification]  = useState("");
  const [businessReason, setBusinessReason] = useState("");
  const [reviewerName,   setReviewerName]   = useState(displayName);
  const [evidenceRef,    setEvidenceRef]     = useState("");
  const [reviewDate,     setReviewDate]      = useState("");
  const [validUntil,     setValidUntil]      = useState("");
  const [reviewDateErr,  setReviewDateErr]   = useState("");
  const [validUntilErr,  setValidUntilErr]   = useState("");

  const validateReviewDate = (v: string) => {
    if (!v) { setReviewDateErr("Risk Review Date is required."); return false; }
    if (v < today) { setReviewDateErr("Review date cannot be in the past."); return false; }
    setReviewDateErr(""); return true;
  };
  const validateValidUntil = (v: string) => {
    if (!v) { setValidUntilErr("Risk Acceptance Valid Until is required."); return false; }
    if (v < today) { setValidUntilErr("Valid until date must be a future date."); return false; }
    setValidUntilErr(""); return true;
  };

  const canSubmit =
    justification.trim().length >= 10 &&
    businessReason.trim().length >= 5 &&
    reviewerName.trim().length > 0 &&
    reviewDate.trim().length > 0 &&
    validUntil.trim().length > 0 &&
    !reviewDateErr && !validUntilErr;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" aria-hidden="true" />
            Accept Risk
          </DialogTitle>
          <DialogDescription>
            The source will be unblocked and allowed for chatbot retrieval. This decision is
            audited and must be documented.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            <strong>Warning:</strong> Accepting risk means this source bypasses the HIPAA security
            check. You must document the justification and business reason.
          </div>
          <div>
            <Label htmlFor="ar-justification" className="text-xs font-medium">
              Risk Justification <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ar-justification"
              className="mt-1 text-xs"
              rows={2}
              placeholder="Why is accepting this risk acceptable? (min. 10 characters)"
              value={justification}
              onChange={e => setJustification(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div>
            <Label htmlFor="ar-business" className="text-xs font-medium">
              Business Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ar-business"
              className="mt-1 text-xs"
              rows={2}
              placeholder="Describe the business context requiring this source (min. 5 characters)"
              value={businessReason}
              onChange={e => setBusinessReason(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div>
            <Label htmlFor="ar-reviewer" className="text-xs font-medium">
              Reviewer Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ar-reviewer"
              className="mt-1 h-8 text-xs"
              placeholder="Full name of the approving reviewer"
              value={reviewerName}
              onChange={e => setReviewerName(e.target.value)}
              maxLength={200}
            />
          </div>
          {/* Required date fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ar-review-date" className="text-xs font-medium">
                Risk Review Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ar-review-date"
                type="date"
                className={`mt-1 h-8 text-xs ${reviewDateErr ? "border-red-400" : ""}`}
                value={reviewDate}
                min={today}
                onChange={e => { setReviewDate(e.target.value); validateReviewDate(e.target.value); }}
              />
              {reviewDateErr && <p className="text-xs text-red-600 mt-0.5">{reviewDateErr}</p>}
            </div>
            <div>
              <Label htmlFor="ar-valid-until" className="text-xs font-medium">
                Risk Acceptance Valid Until <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ar-valid-until"
                type="date"
                className={`mt-1 h-8 text-xs ${validUntilErr ? "border-red-400" : ""}`}
                value={validUntil}
                min={today}
                onChange={e => { setValidUntil(e.target.value); validateValidUntil(e.target.value); }}
              />
              {validUntilErr && <p className="text-xs text-red-600 mt-0.5">{validUntilErr}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="ar-evidence" className="text-xs font-medium text-muted-foreground">
              Evidence Reference <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="ar-evidence"
              className="mt-1 h-8 text-xs font-mono"
              placeholder="Link or reference to supporting evidence"
              value={evidenceRef}
              onChange={e => setEvidenceRef(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => {
              const rdOk = validateReviewDate(reviewDate);
              const vuOk = validateValidUntil(validUntil);
              if (rdOk && vuOk) onSubmit({ justification, businessReason, reviewerName, evidenceRef, reviewDate, validUntil });
            }}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Saving…" : "Accept Risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExceptionDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (data: {
    justification: string; approvalJustification: string;
    approverName: string; approverTitle: string;
    approvalDate: string; expirationDate: string;
  }) => void;
  submitting: boolean;
}) {
  const { displayName } = useCurrentUserInfo();
  const [justification, setJustification] = useState("");
  const [approvalJustification, setApprovalJustification] = useState("");
  const [approverName, setApproverName] = useState(displayName);
  const [approverTitle, setApproverTitle] = useState("");
  const [approvalDate, setApprovalDate] = useState(new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState("");
  const [expiryErr, setExpiryErr] = useState("");

  const validateExpiry = (v: string) => {
    if (!v) { setExpiryErr("Expiration date is required."); return; }
    if (new Date(v) <= new Date()) { setExpiryErr("Expiration date must be in the future."); return; }
    setExpiryErr("");
  };

  const canSubmit =
    justification.trim().length >= 10 &&
    approvalJustification.trim().length >= 10 &&
    approverName.trim().length > 0 &&
    expirationDate.trim().length > 0 &&
    !expiryErr;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600" aria-hidden="true" />
            Approve Exception
          </DialogTitle>
          <DialogDescription>
            A formal exception grants this source access despite HIPAA check failures. Full audit
            trail is recorded and the exception auto-expires on the set date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label htmlFor="ex-reason" className="text-xs font-medium">
              Exception Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ex-reason"
              className="mt-1 text-xs"
              rows={2}
              placeholder="Why is an exception required? (min. 10 characters)"
              value={justification}
              onChange={e => setJustification(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div>
            <Label htmlFor="ex-approval-just" className="text-xs font-medium">
              Approval Justification <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="ex-approval-just"
              className="mt-1 text-xs"
              rows={2}
              placeholder="Describe mitigating controls or compensating measures (min. 10 characters)"
              value={approvalJustification}
              onChange={e => setApprovalJustification(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ex-approver" className="text-xs font-medium">
                Reviewer Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ex-approver"
                className="mt-1 h-8 text-xs"
                placeholder="Full name"
                value={approverName}
                onChange={e => setApproverName(e.target.value)}
                maxLength={200}
              />
            </div>
            <div>
              <Label htmlFor="ex-title" className="text-xs font-medium">Reviewer Title</Label>
              <Input
                id="ex-title"
                className="mt-1 h-8 text-xs"
                placeholder="e.g. CISO"
                value={approverTitle}
                onChange={e => setApproverTitle(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ex-date" className="text-xs font-medium">Approval Date</Label>
              <Input
                id="ex-date"
                type="date"
                className="mt-1 h-8 text-xs"
                value={approvalDate}
                onChange={e => setApprovalDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ex-exp" className="text-xs font-medium">
                Expiration Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ex-exp"
                type="date"
                className={`mt-1 h-8 text-xs ${expiryErr ? "border-red-400" : ""}`}
                value={expirationDate}
                onChange={e => { setExpirationDate(e.target.value); validateExpiry(e.target.value); }}
                min={new Date().toISOString().slice(0, 10)}
              />
              {expiryErr && <p className="text-xs text-red-600 mt-0.5">{expiryErr}</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => onSubmit({
              justification: `${justification}\n\nApproval justification: ${approvalJustification}`,
              approvalJustification,
              approverName, approverTitle, approvalDate, expirationDate,
            })}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Saving…" : "Approve Exception"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeepBlockedDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (data: { justification: string }) => void;
  submitting: boolean;
}) {
  const [justification, setJustification] = useState("");
  const canSubmit = justification.trim().length >= 5;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            Keep Blocked
          </DialogTitle>
          <DialogDescription>
            This will keep the source blocked from chatbot retrieval. A record of this decision is
            added to the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded bg-muted/60 border px-3 py-2 text-xs text-muted-foreground mb-3">
          <Info className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />
          This source will remain excluded from all chatbot knowledge retrieval.
        </div>
        <div>
          <Label htmlFor="kb-reason" className="text-xs font-medium">
            Reason <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="kb-reason"
            className="mt-1 text-xs"
            rows={3}
            placeholder="Why is this source being kept blocked? (min. 5 characters)"
            value={justification}
            onChange={e => setJustification(e.target.value)}
            maxLength={2000}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="secondary" size="sm"
            onClick={() => onSubmit({ justification })} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Confirm Keep Blocked"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Structured Decision Panel (blocked source) ────────────────────────────────
function DecisionPanel({
  verification, canManage, onDecisionMade, onReverify, reverifying,
}: {
  verification: SourceVerification; canManage: boolean; onDecisionMade: () => void;
  onReverify: (sourceId: string) => void; reverifying: boolean;
}) {
  const authFetch = useHipaaFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<DecisionModal>(null);

  const failedAuto  = verification.controlResults.filter(
    r => r.verificationMethod === "automatic" && r.complianceStatus === "failed",
  );
  const needEvidence = verification.controlResults.filter(
    r => r.complianceStatus === "manual_required",
  );

  const decisionM = useMutation({
    mutationFn: async (payload: Record<string, string | undefined>) => {
      const r = await authFetch(
        `/api/organization/compliance/hipaa/source-verifications/${verification.id}/decisions`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error((e as any).message ?? "Decision failed");
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Decision recorded." });
      qc.invalidateQueries({ queryKey: ["hipaa-source-verifications"] });
      qc.invalidateQueries({ queryKey: ["hipaa-decisions", verification.id] });
      setModal(null);
      onDecisionMade();
    },
    onError: (e: any) =>
      toast({ title: e?.message ?? "Unable to save decision.", variant: "destructive" }),
  });

  const submit = (decisionType: string, extra: Record<string, string | undefined> = {}) =>
    decisionM.mutate({ decisionType, ...extra });

  const isBlocked = verification.overallStatus === "blocked";
  const isAllowed = verification.overallStatus === "allowed";

  return (
    <div className="space-y-3">
      {/* ── HIPAA Status ── */}
      <div className="rounded-md border px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            HIPAA Status
          </span>
          {isBlocked && <Badge variant="destructive" className="text-xs">🔴 Blocked</Badge>}
          {isAllowed && <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Allowed</Badge>}
        </div>

        {/* Reason row */}
        {isBlocked && (failedAuto.length > 0 || needEvidence.length > 0) && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p className="font-medium text-foreground text-xs">Reason:</p>
            {failedAuto.length > 0 && (
              <p className="text-red-700">
                {failedAuto.length} failed security control{failedAuto.length !== 1 ? "s" : ""}
              </p>
            )}
            {needEvidence.length > 0 && (
              <p className="text-amber-700">
                {needEvidence.length} control{needEvidence.length !== 1 ? "s" : ""} require evidence
              </p>
            )}
          </div>
        )}

        {/* Failed Controls list */}
        {isBlocked && failedAuto.length > 0 && (
          <div className="text-xs space-y-1">
            <p className="font-medium">Failed Controls:</p>
            <div className="space-y-1">
              {failedAuto.map(fc => (
                <div key={fc.controlKey} className="rounded bg-red-50 border border-red-200 px-2 py-1.5">
                  <p className="font-medium text-red-800">— {fc.controlName}</p>
                  <p className="text-red-700 text-[10px]">
                    Status: <span className="font-mono uppercase">Failed</span>
                  </p>
                  {fc.findings && (
                    <p className="text-red-600 text-[10px]">Reason: {fc.findings}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Compliance Decision */}
        <div>
          <p className="text-xs font-medium mb-1">Compliance Decision:</p>
          <CurrentDecisionPanel verificationId={verification.id} />
          {/* Fallback if no decision yet */}
          <_NullDecisionPlaceholder verificationId={verification.id} />
        </div>
      </div>

      {/* ── Available Actions (canManage + blocked only) ── */}
      {canManage && isBlocked && (
        <div className="rounded-md border border-dashed px-3 py-2.5">
          <p className="text-xs font-semibold mb-2">Available Actions:</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-50"
              onClick={() => setModal("fix_required")}
            >
              <Wrench className="w-3 h-3 mr-1" aria-hidden="true" /> Fix Required
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => setModal("accept_risk")}
            >
              <ShieldAlert className="w-3 h-3 mr-1" aria-hidden="true" /> Accept Risk
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-50"
              onClick={() => setModal("exception_approved")}
            >
              <ShieldCheck className="w-3 h-3 mr-1" aria-hidden="true" /> Approve Exception
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setModal("keep_blocked")}
            >
              <ShieldOff className="w-3 h-3 mr-1" aria-hidden="true" /> Keep Blocked
            </Button>
          </div>
        </div>
      )}

      {/* Allowed banner + Re-verify (canManage) */}
      {canManage && isAllowed && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-2">
          <p>
            <strong>Risk accepted / Exception approved.</strong> This source is allowed for chatbot
            retrieval despite failing HIPAA checks. Re-verify or change this decision to re-block.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
            onClick={() => onReverify(verification.knowledgeSourceId)}
            disabled={reverifying}
          >
            <RefreshCcw className={`w-3 h-3 mr-1 ${reverifying ? "animate-spin" : ""}`} aria-hidden="true" />
            {reverifying ? "Re-verifying…" : "Re-verify"}
          </Button>
        </div>
      )}

      {/* Modals */}
      <FixRequiredDialog
        open={modal === "fix_required"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification }) => submit("remediation_required", { justification })}
      />
      <AcceptRiskDialog
        open={modal === "accept_risk"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification, businessReason, reviewerName, evidenceRef, reviewDate, validUntil }) =>
          submit("accept_risk", {
            justification: JSON.stringify({ justification, businessReason, evidenceRef, reviewDate, validUntil }),
            approverName: reviewerName,
            expirationDate: validUntil,  // store as expirationDate in DB for backward compat
          })}
      />
      <ExceptionDialog
        open={modal === "exception_approved"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification, approverName, approverTitle, approvalDate, expirationDate }) =>
          submit("exception_approved", { justification, approverName, approverTitle, approvalDate, expirationDate })}
      />
      <KeepBlockedDialog
        open={modal === "keep_blocked"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification }) => submit("keep_blocked", { justification })}
      />
    </div>
  );
}

// Sub-component: shows "No decision taken yet" only when there really are no decisions
function _NullDecisionPlaceholder({ verificationId }: { verificationId: string }) {
  const authFetch = useHipaaFetch();
  const { data } = useQuery<ComplianceDecision[]>({
    queryKey: ["hipaa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/hipaa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000,
  });
  if (!data || data.length > 0) return null;
  return (
    <p className="text-xs text-muted-foreground italic">No decision taken yet.</p>
  );
}

// ── Full verification card (nested inside a bot group) ────────────────────────
function VerificationCard({
  v, canManage, onReverify, reverifying, onRefresh,
}: {
  v: SourceVerification;
  canManage: boolean;
  onReverify: (id: string) => void;
  reverifying: boolean;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const showDecisionPanel = v.overallStatus === "blocked" || v.overallStatus === "allowed";

  // Sort controls: failed → pending → passed → n/a for priority-first layout
  const sorted = [...v.controlResults].sort((a, b) => {
    const rank = (s: string) =>
      s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
    return rank(a.complianceStatus) - rank(b.complianceStatus);
  });

  // Group by category (linkedEntityType → label, or "Other Controls")
  const byCategory = new Map<string, ControlResult[]>();
  for (const r of sorted) {
    const cat = r.linkedEntityType
      ? (ENTITY_TYPE_LABELS[r.linkedEntityType] ?? r.linkedEntityType)
      : "Controls";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(r);
  }
  // Flatten back with category headers
  const categoryGroups = Array.from(byCategory.entries());

  return (
    <div className="border rounded-lg overflow-hidden ml-4 bg-white">
      {/* ── Redesigned header ── */}
      <div
        className="flex items-start gap-3 px-3 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0 space-y-1">
          {/* Title row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">{v.sourceName}</span>
            <Badge variant="outline" className="text-[10px] h-4 px-1">{v.sourceType}</Badge>
            {statusBadge(v.overallStatus)}
          </div>
          {/* Stats row */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            {v.passedCount > 0 && (
              <span className="flex items-center gap-1 text-green-700">
                <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                {v.passedCount} passed
              </span>
            )}
            {v.failedCount > 0 && (
              <span className="flex items-center gap-1 text-red-600 font-medium">
                <XCircle className="w-3 h-3" aria-hidden="true" />
                {v.failedCount} failed
              </span>
            )}
            {v.manualRequiredCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Clock className="w-3 h-3" aria-hidden="true" />
                {v.manualRequiredCount} need evidence
              </span>
            )}
            {v.verificationCompletedAt && (
              <span className="text-muted-foreground/70">
                Verified {new Date(v.verificationCompletedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
          {canManage && (v.overallStatus === "blocked" || v.overallStatus === "pending_review") && (
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={e => { e.stopPropagation(); onReverify(v.knowledgeSourceId); }}
              disabled={reverifying}
            >
              <RefreshCcw className={`w-3 h-3 mr-1 ${reverifying ? "animate-spin" : ""}`} aria-hidden="true" />
              Re-verify
            </Button>
          )}
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="border-t px-3 py-3 space-y-4 bg-muted/10">

          {/* HIPAA Compliance Details header */}
          <div className="rounded-md border border-muted bg-white px-3 py-2.5 space-y-1 text-xs">
            <p className="font-bold text-sm text-foreground">HIPAA Compliance Details</p>
            <div className="grid grid-cols-1 gap-0.5 text-xs text-muted-foreground">
              <p><span className="font-medium text-foreground">Knowledge Source:</span> {v.sourceName}</p>
              <p>
                <span className="font-medium text-foreground">Status:</span>{" "}
                {statusBadge(v.overallStatus)}
              </p>
            </div>
          </div>

          {/* Decision panel (blocked/allowed) */}
          {showDecisionPanel && (
            <DecisionPanel
              verification={v}
              canManage={canManage}
              onDecisionMade={onRefresh}
              onReverify={onReverify}
              reverifying={reverifying}
            />
          )}

          {/* Pending review banner */}
          {v.overallStatus === "pending_review" && (
            <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <strong>Evidence required.</strong> Automatic checks passed. Supply evidence
              references for the manual controls below.
            </div>
          )}

          {/* Controls as proper compliance cards, grouped by category */}
          {v.controlResults.length > 0 && (
            <div className="space-y-3">
              {categoryGroups.map(([cat, controls]) => (
                <div key={cat}>
                  {categoryGroups.length > 1 && (
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" aria-hidden="true" />
                      {cat}
                    </p>
                  )}
                  <div className="space-y-2">
                    {controls.map(r => (
                      <ControlComplianceCard
                        key={r.id}
                        r={r}
                        sourceId={v.knowledgeSourceId}
                        canManage={canManage}
                        onEvidenceSaved={onRefresh}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Decision history */}
          <DecisionHistoryPanel verificationId={v.id} />

          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Link2 className="w-3 h-3" aria-hidden="true" />
            Source ID: <code className="font-mono">{v.knowledgeSourceId}</code>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bot-level group card ──────────────────────────────────────────────────────
function BotGroupCard({
  botId, botName, sources, canManage, onReverify, reverifyingId, onRefresh,
}: {
  botId: string;
  botName: string;
  sources: SourceVerification[];
  canManage: boolean;
  onReverify: (sourceId: string) => void;
  reverifyingId: string | undefined;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate compliance stats across all knowledge sources for this bot
  const totalPassed   = sources.reduce((s, v) => s + v.passedCount, 0);
  const totalFailed   = sources.reduce((s, v) => s + v.failedCount, 0);
  const totalPending  = sources.reduce((s, v) => s + v.manualRequiredCount, 0);
  const riskAccepted  = sources.filter(v => v.overallStatus === "allowed").length;
  const blockedCount  = sources.filter(v => v.overallStatus === "blocked").length;
  const totalControls = totalPassed + totalFailed;
  const compliancePct = totalControls > 0 ? Math.round((totalPassed / totalControls) * 100) : 100;

  // Compliance colour
  const pctColor =
    compliancePct >= 90 ? "text-green-700" :
    compliancePct >= 70 ? "text-amber-600" :
                          "text-red-600";

  return (
    <div className="border rounded-lg overflow-hidden shadow-sm">
      {/* Bot header */}
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors bg-slate-50"
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          {/* Bot name + source count */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-sm">{botName}</span>
            <Badge variant="outline" className="text-xs">
              {sources.length} Knowledge Source{sources.length !== 1 ? "s" : ""}
            </Badge>
            {blockedCount  > 0 && <Badge variant="destructive" className="text-xs">{blockedCount} blocked</Badge>}
            {riskAccepted  > 0 && <Badge className="text-xs bg-amber-600 hover:bg-amber-600">{riskAccepted} risk accepted</Badge>}
          </div>
          {/* Compliance stats grid */}
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="flex items-center gap-1 text-green-700">
              <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
              {totalPassed} passed
            </span>
            {totalFailed > 0 && (
              <span className="flex items-center gap-1 text-red-600 font-medium">
                <XCircle className="w-3 h-3" aria-hidden="true" />
                {totalFailed} failed
              </span>
            )}
            {totalPending > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Clock className="w-3 h-3" aria-hidden="true" />
                {totalPending} need evidence
              </span>
            )}
            <span className={`font-semibold ${pctColor}`}>
              {compliancePct}% compliant
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 pt-0.5">
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
        </div>
      </div>

      {/* Expanded: knowledge sources subsection */}
      {expanded && (
        <div className="border-t bg-muted/5">
          <div className="px-4 py-2 border-b bg-muted/20">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Knowledge Sources
            </p>
          </div>
          <div className="px-3 py-3 space-y-2">
            {sources.map(v => (
              <VerificationCard
                key={v.id}
                v={v}
                canManage={canManage}
                onReverify={onReverify}
                reverifying={reverifyingId === v.knowledgeSourceId}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Sentinel for verifications whose chatbot cannot be resolved
export const UNKNOWN_BOT_ID = "__unknown__";

// ── Exported hook: bot-grouped verification data (shared by hipaa-tab.tsx) ─────
export function useBotVerificationsData() {
  const authFetch = useHipaaFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa";

  // ── 1. Chatbot list ─────────────────────────────────────────────────────────
  const { data: chatbotsData, isLoading: botsLoading } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["chatbots-list-hipaa"],
    queryFn: async () => {
      const r = await authFetch("/api/chatbots");
      if (!r.ok) return [];
      const json = await r.json();
      return Array.isArray(json) ? json : (json.items ?? json.chatbots ?? []);
    },
    staleTime: 60_000,
  });

  // ── 2. HIPAA verifications ─────────────────────────────────────────────────
  const { data: verifications, isLoading: verificationsLoading } = useQuery<SourceVerification[]>({
    queryKey: ["hipaa-source-verifications"],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/source-verifications`);
      if (!r.ok) throw new Error("Failed to load HIPAA source verifications");
      return r.json();
    },
    staleTime: 10_000,
    refetchInterval: (query) => {
      const rows = query.state.data as SourceVerification[] | undefined;
      return rows?.some(v => v.overallStatus === "pending_verification") ? 3000 : false;
    },
  });

  // ── 3. Knowledge source ownership map ─────────────────────────────────────
  // Fetch /api/chatbots/{id}/knowledge for every bot in parallel so we can
  // use KnowledgeSource.ChatbotId as the authoritative grouping key instead
  // of SourceVerification.ChatbotId (which can be stale/wrong in the DB).
  const botIds = (chatbotsData ?? []).map(b => b.id);
  const { data: ksOwnerMap, isLoading: ksLoading } = useQuery<Map<string, string>>({
    queryKey: ["hipaa-ks-ownership", botIds.slice().sort().join(",")],
    enabled: botIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        botIds.map(async (botId) => {
          try {
            const r = await authFetch(`/api/chatbots/${botId}/knowledge`);
            if (!r.ok) return [] as [string, string][];
            const json = await r.json();
            const items: { id: string }[] = Array.isArray(json) ? json : (json.items ?? []);
            // Normalize both sides to lowercase to avoid UUID case mismatches
            return items.map(ks => [ks.id.toLowerCase(), botId.toLowerCase()] as [string, string]);
          } catch {
            return [] as [string, string][];
          }
        }),
      );
      return new Map<string, string>(results.flat());
    },
    staleTime: 30_000,
  });

  // ── Build name map (lowercase-keyed for safe lookup) ──────────────────────
  const botNameMap = new Map<string, string>(
    (chatbotsData ?? []).map(b => [b.id.toLowerCase(), b.name]),
  );

  // ── Mutation ───────────────────────────────────────────────────────────────
  const refresh = () => qc.invalidateQueries({ queryKey: ["hipaa-source-verifications"] });

  const reverifyM = useMutation({
    mutationFn: (sourceId: string) =>
      authFetch(`${BASE}/source-verifications/${sourceId}/reverify`, { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Re-verification queued. The status will update shortly." });
      refresh();
    },
    onError: () => toast({ title: "Re-verification failed.", variant: "destructive" }),
  });

  // ── Group verifications strictly by KS ownership — NO fallback ───────────────
  // IMPORTANT: we deliberately do NOT fall back to verification.chatbotId.
  // That field can be stale and causes cross-bot source leakage.
  // Truth comes exclusively from ksOwnerMap (built from /api/chatbots/{id}/knowledge).
  // If ksOwnerMap is still loading (undefined), we hold off — the isLoading gate
  // in the consumer keeps the UI blank until all three fetches resolve.
  const botOrder: string[] = botIds.map(id => id.toLowerCase());

  const byBot = new Map<string, SourceVerification[]>();
  for (const botId of botOrder) byBot.set(botId, []);

  for (const v of (verifications ?? [])) {
    const ksId = v.knowledgeSourceId?.toLowerCase() ?? "";
    // Strict lookup — undefined if the KS API hasn't seen this source id
    const resolvedId = ksOwnerMap?.get(ksId);  // NO ?? fallback

    if (resolvedId && byBot.has(resolvedId)) {
      byBot.get(resolvedId)!.push(v);
    } else {
      // KS not found in any bot's list (or map still loading) → unknown sentinel
      if (!byBot.has(UNKNOWN_BOT_ID)) {
        botOrder.push(UNKNOWN_BOT_ID);
        byBot.set(UNKNOWN_BOT_ID, []);
      }
      byBot.get(UNKNOWN_BOT_ID)!.push(v);
    }
  }

  // ── Debug: log per-bot source assignment (temporary) ──────────────────────
  if (ksOwnerMap) {
    for (const botId of botOrder) {
      const name    = botNameMap.get(botId) ?? (botId === UNKNOWN_BOT_ID ? "Unknown Bot" : botId);
      const sources = byBot.get(botId) ?? [];
      console.log("[HIPAA grouping]", {
        botId,
        botName: name,
        sources: sources.map(x => ({ id: x.knowledgeSourceId, name: x.sourceName })),
      });
    }
  }

  return {
    isLoading: verificationsLoading || botsLoading || (botIds.length > 0 && ksLoading),
    botOrder,
    byBot,
    botNameMap,
    reverifyM,
    refresh,
  };
}

// ── Exported component: single-bot knowledge sources tab content ───────────────
export function SingleBotKnowledgeSourcesTab({
  sources, canManage, onReverify, reverifyingId, onRefresh,
}: {
  sources: SourceVerification[];
  canManage: boolean;
  onReverify: (sourceId: string) => void;
  reverifyingId: string | undefined;
  onRefresh: () => void;
}) {
  if (sources.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground py-10">
        No knowledge sources attached to this bot yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {sources.map(v => (
        <VerificationCard
          key={v.id}
          v={v}
          canManage={canManage}
          onReverify={onReverify}
          reverifying={reverifyingId === v.knowledgeSourceId}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
}

// ── Legacy section (kept for backward compat — no longer used in HipaaTab) ────
export function HipaaSourceVerificationsSection({ canManage }: { canManage: boolean }) {
  const { isLoading, botOrder, byBot, botNameMap, reverifyM, refresh } = useBotVerificationsData();

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (botOrder.length === 0) return null;

  const allSources  = botOrder.flatMap(id => byBot.get(id) ?? []);
  const blockedCount  = allSources.filter(v => v.overallStatus === "blocked").length;
  const allowedCount  = allSources.filter(v => v.overallStatus === "allowed").length;
  const reviewCount   = allSources.filter(v => v.overallStatus === "pending_review").length;
  const verifiedCount = allSources.filter(v => v.overallStatus === "active").length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Knowledge Source HIPAA Verifications</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Automatically verified when knowledge sources are added. Blocked sources are excluded
              from chatbot retrieval unless a compliance officer accepts the risk or approves an exception.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {verifiedCount > 0 && <Badge variant="default">{verifiedCount} verified</Badge>}
            {blockedCount  > 0 && <Badge variant="destructive">{blockedCount} blocked</Badge>}
            {allowedCount  > 0 && <Badge className="bg-amber-600 hover:bg-amber-600">{allowedCount} risk accepted</Badge>}
            {reviewCount   > 0 && <Badge variant="secondary">{reviewCount} need review</Badge>}
            <Badge variant="outline">{allSources.length} total</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {botOrder.map(botId => (
          <BotGroupCard
            key={botId}
            botId={botId}
            botName={botNameMap.get(botId) ?? "Unknown Bot"}
            sources={byBot.get(botId) ?? []}
            canManage={canManage}
            onReverify={id => reverifyM.mutate(id)}
            reverifyingId={reverifyM.isPending ? reverifyM.variables : undefined}
            onRefresh={refresh}
          />
        ))}
      </CardContent>
    </Card>
  );
}
